import React, { useMemo, useState, useEffect } from "react";
import moment from "moment";

import "./WSGRSteps.scss";
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { portfoliosData } from "../../portfoliosData";
import { useAuth } from "../../../../contexts/Auth";
import { connect } from "react-redux";
import { wealthRecentRebalancesLoad } from "../../../../appRedux/ducks/wealthRecentRebalances";
import { wealthGuidedRebalancesSubmit } from "../../../../appRedux/ducks/wealthGuidedRebalances";
import { wealthGuidedRebalancesGet } from "../../../../appRedux/ducks/wealthGuidedRebalancesGet";

const WSGRStep1 = ({
  setStep,
  tier,
  portfolios: portfoliosProp,
  tierDisplayName,
  wealthPortfolios,
  wealthRecentRebalances,
  selectedPortfolio,
  setSelectedPortfolio,
  selectedDate,
  setSelectedDate,
  loadRecentRebalances,
  submitGuidedRebalance,
  fetchGuidedRebalances,
  wealthGuidedRebalances,
  onClose,
}) => {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const {
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  } = useAuth();

  // Determine if user has auto portfolios for current tier
  const hasAutoPortfolios = useMemo(() => {
    if (tier === "atlas") return hasWealthSeriesAtlasAuto;
    if (tier === "titan") return hasWealthSeriesTitanAuto;
    if (tier === "everest") return hasWealthSeriesEverestAuto;
    return false;
  }, [
    tier,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  ]);

  // Check if auto portfolios are actually loaded in the data
  const hasAutoPortfoliosLoaded = useMemo(() => {
    return Object.keys(wealthPortfolios).some(
      (key) => key.includes("_auto") && key.includes(tier),
    );
  }, [wealthPortfolios, tier]);

  // Should use auto portfolios if user has access AND they're loaded
  const shouldUseAutoPortfolios = hasAutoPortfolios && hasAutoPortfoliosLoaded;

  // Get portfolios with proper IDs based on subscription. Products outside
  // the wealth series portfoliosData tables (e.g. Bitcoin Navigator) inject
  // their own [{ id, name }] list instead.
  const portfolios = useMemo(() => {
    if (portfoliosProp?.length) {
      return portfoliosProp;
    }
    return (
      portfoliosData[tier]?.portfolios?.map((portfolio) => ({
        id: shouldUseAutoPortfolios ? portfolio.autoId : portfolio.id,
        name: portfolio.name,
        url: shouldUseAutoPortfolios ? `${portfolio.url}_auto` : portfolio.url,
        isAuto: shouldUseAutoPortfolios,
      })) || []
    );
  }, [tier, shouldUseAutoPortfolios, portfoliosProp]);

  // Get unique dates from Redux data for the selected portfolio
  const uniqueDates = useMemo(() => {
    if (
      !selectedPortfolio ||
      !wealthRecentRebalances?.recentRebalances?.[tier]
    ) {
      return [];
    }

    const rebalanceData = wealthRecentRebalances.recentRebalances[tier];
    const portfolioRebalances = rebalanceData[selectedPortfolio];

    if (!portfolioRebalances || !Array.isArray(portfolioRebalances)) {
      return [];
    }

    // Extract and format dates
    return portfolioRebalances
      .map((item) => moment(item.rebalance_date).format("YYYY-MM-DD"))
      .sort((a, b) => new Date(b) - new Date(a)); // Sort by most recent first
  }, [selectedPortfolio, wealthRecentRebalances, tier]);

  const getPortfolioName = (portfolioId) => {
    return portfolios.find((portfolio) => portfolio.id === portfolioId)?.name;
  };

  // Clear selected date when portfolio changes or when dates change
  React.useEffect(() => {
    if (selectedDate && !uniqueDates.includes(selectedDate)) {
      setSelectedDate("");
    }
  }, [uniqueDates, selectedDate, setSelectedDate, tier]);

  // Get default AUM by tier
  const getDefaultAum = (tier) => {
    if (tier === "core") return 30000;
    if (tier === "atlas") return 50000;
    if (tier === "titan") return 100000;
    if (tier === "everest") return 100000;
    return 50000; // fallback
  };

  // Find the rebalance_id from the selected date and portfolio
  const selectedRebalanceId = useMemo(() => {
    if (
      !selectedPortfolio ||
      !selectedDate ||
      !wealthRecentRebalances?.recentRebalances?.[tier]
    ) {
      return null;
    }

    const portfolioRebalances =
      wealthRecentRebalances.recentRebalances[tier][selectedPortfolio];

    if (!portfolioRebalances || !Array.isArray(portfolioRebalances)) {
      return null;
    }

    // Find the rebalance that matches our selected date
    const matchingRebalance = portfolioRebalances.find(
      (item) =>
        moment(item.rebalance_date).format("YYYY-MM-DD") === selectedDate,
    );

    return matchingRebalance?.rebalance_id || null;
  }, [selectedPortfolio, selectedDate, wealthRecentRebalances, tier]);

  // Get loading state from Redux
  const isLoading = wealthGuidedRebalances?.loading?.[tier] || false;

  // Handle form submission
  const handleSubmit = () => {
    if (!selectedRebalanceId || isLoading) return;

    const payload = {
      portfolio_id: selectedPortfolio,
      rebalance_id: selectedRebalanceId,
      aum_allocation: getDefaultAum(tier),
    };

    setHasSubmitted(true);
    submitGuidedRebalance(tier, payload);
  };

  // Handle successful submission - fetch data and move to step 3
  useEffect(() => {
    if (
      wealthGuidedRebalances?.guidedRebalances?.[tier] &&
      !isLoading &&
      hasSubmitted
    ) {
      // POST was successful, now fetch the guided rebalances data
      fetchGuidedRebalances(tier);
      setStep(3);
      setHasSubmitted(false); // Reset the flag
    }
  }, [
    wealthGuidedRebalances,
    tier,
    isLoading,
    setStep,
    fetchGuidedRebalances,
    hasSubmitted,
  ]);

  return (
    <div className="ws-gr-step-1">
      <div className="ws-gr-step-1-content">
        <div className="ws-gr-step-counter-mobile">Step 1 of 2</div>

        <div className="ws-gr-step-1-header">
          <div className="ws-gr-step-1-content-title">
            Select a portfolio you follow in{" "}
            {tierDisplayName || tier.charAt(0).toUpperCase() + tier.slice(1)}
          </div>
          <IconButton
            className="ws-gr-close-button"
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="ws-gr-step-1-content-portfolios-list">
          <RadioGroup
            column
            className="ws-gr-step-1-content-portfolios"
            value={selectedPortfolio}
            onChange={(e) => setSelectedPortfolio(e.target.value)}
          >
            {portfolios.map((portfolio) => (
              <FormControlLabel
                value={portfolio.id}
                control={<Radio />}
                label={portfolio.name}
                key={portfolio.id}
                className="ws-gr-step-1-radio"
              />
            ))}
          </RadioGroup>
        </div>

        {selectedPortfolio && (
          <div className="ws-gr-step-1-dates">
            <div className="ws-gr-step-1-dates-header">
              Last Adjusted Date for {getPortfolioName(selectedPortfolio)}
            </div>

            {wealthRecentRebalances?.loading?.[tier] ? (
              <div className="ws-gr-step-1-loading">
                Loading rebalance dates...
              </div>
            ) : wealthRecentRebalances?.error?.[tier] ? (
              <div className="ws-gr-step-1-error">
                <div>Failed to load rebalance dates.</div>
                <button
                  onClick={() => loadRecentRebalances(tier)}
                  style={{
                    marginTop: "8px",
                    padding: "4px 8px",
                    cursor: "pointer",
                  }}
                >
                  Retry
                </button>
              </div>
            ) : uniqueDates.length > 0 ? (
              <RadioGroup
                column
                className="ws-gr-step-1-content-portfolios dates-radio-group"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              >
                {uniqueDates.map((date) => (
                  <FormControlLabel
                    value={date}
                    control={<Radio />}
                    label={moment(date).format("MMMM DD, YYYY")}
                    key={date}
                    className="ws-gr-step-1-radio"
                  />
                ))}
              </RadioGroup>
            ) : (
              <div className="ws-gr-step-1-no-dates">
                No recent rebalance dates available for this portfolio.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ws-gr-step-footer">
        <div className="ws-gr-step-indicator">Step 1 of 2</div>
        <Button
          className="ws-gr-step-footer-next-button"
          endIcon={isLoading ? null : <ArrowForwardIcon />}
          disabled={
            selectedPortfolio === "" ||
            selectedDate === "" ||
            uniqueDates.length === 0 ||
            !selectedRebalanceId ||
            isLoading
          }
          onClick={handleSubmit}
        >
          {isLoading ? "Loading..." : "Get adjustments"}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  wealthGuidedRebalances: state.wealthGuidedRebalances,
});

const mapDispatchToProps = (dispatch) => ({
  loadRecentRebalances: (tier) => dispatch(wealthRecentRebalancesLoad(tier)),
  submitGuidedRebalance: (tier, payload) =>
    dispatch(wealthGuidedRebalancesSubmit(tier, payload)),
  fetchGuidedRebalances: (tier) => dispatch(wealthGuidedRebalancesGet(tier)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WSGRStep1);
